import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  colorScheme?: 'gray' | 'orange' | 'yellow';
  baseUrl?: string; // Base URL for converting relative links to absolute
  compact?: boolean; // Tighter spacing for cards/inline contexts
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className = '',
  colorScheme = 'gray',
  baseUrl,
  compact = false,
}) => {
  const getColorClasses = (colorScheme: string) => {
    const colors: Record<string, { link: string; blockquote: string }> = {
      gray: {
        link: 'text-brand-500 hover:text-brand-700',
        blockquote: 'border-yellow-400'
      },
      orange: {
        link: 'text-brand-500 hover:text-brand-700',
        blockquote: 'border-brand-400'
      },
      yellow: {
        link: 'text-brand-500 hover:text-brand-700',
        blockquote: 'border-yellow-400'
      }
    };
    return colors[colorScheme] || colors.gray;
  };

  const colors = getColorClasses(colorScheme);

  // Helper function to convert relative URLs to absolute URLs
  const getAbsoluteUrl = (href: string | undefined) => {
    if (!href) return '#';
    
    // If already absolute URL, return as is
    if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('mailto:') || href.startsWith('tel:')) {
      return href;
    }
    
    // If we have a baseUrl and href is relative, combine them
    if (baseUrl && href.startsWith('/')) {
      // Remove trailing slash from baseUrl if it exists
      const cleanBaseUrl = baseUrl.replace(/\/$/, '');
      return `https://${cleanBaseUrl}${href}`;
    }
    
    // If baseUrl and relative path without leading slash
    if (baseUrl && !href.startsWith('/')) {
      const cleanBaseUrl = baseUrl.replace(/\/$/, '');
      return `https://${cleanBaseUrl}/${href}`;
    }
    
    // Return as is if no baseUrl provided
    return href;
  };

  // Spacing helpers — compact mode uses tight spacing for cards/inline
  const sp = compact
    ? { h1mt: 'mt-4', h1mb: 'mb-2', h2mt: 'mt-3', h2mb: 'mb-1.5', h3mt: 'mt-2', h3mb: 'mb-1',
        h4mt: 'mt-2', h4mb: 'mb-1', pmt: 'mt-0.5', pmb: 'mb-2', lead: 'leading-relaxed',
        ulmt: 'mt-1', ulmb: 'mb-2', liml: 'ml-2', limb: 'mb-0.5', bqmy: 'my-2',
        premy: 'my-2', tblmy: 'my-2', hrmy: 'my-3',
        h1sz: 'text-[17px]', h2sz: 'text-[15px]', h3sz: 'text-sm', h4sz: 'text-sm',
        codesz: 'text-xs', presz: 'p-2.5', bqpl: 'pl-3 pr-2 py-1.5' }
    : { h1mt: 'mt-8', h1mb: 'mb-6', h2mt: 'mt-8', h2mb: 'mb-5', h3mt: 'mt-6', h3mb: 'mb-4',
        h4mt: 'mt-5', h4mb: 'mb-3', pmt: 'mt-2', pmb: 'mb-5', lead: 'leading-7',
        ulmt: 'mt-3', ulmb: 'mb-6', liml: 'ml-4', limb: 'mb-2', bqmy: 'my-6',
        premy: 'my-6', tblmy: 'my-6', hrmy: 'my-8',
        h1sz: 'text-2xl', h2sz: 'text-xl', h3sz: 'text-lg', h4sz: 'text-base',
        codesz: 'text-sm', presz: 'p-4', bqpl: 'pl-6 pr-4 py-3' };

  // Preprocess markdown: Add line breaks after bold text in list items
  // Transforms "- **Title** text" to "- **Title**  \n  text"
  // (Two spaces before newline creates hard break in markdown)
  const processedContent = content.replace(
    /^(\s*[-*]\s+\*\*[^*]+\*\*)\s+(.+)$/gm,
    '$1  \n  $2'
  );

  return (
    <div className={`markdown-content prose prose-${colorScheme} max-w-none break-words overflow-hidden prose-img:rounded-lg prose-img:shadow-md prose-headings:font-semibold prose-a:text-brand-500 hover:prose-a:text-brand-700 [&>*:first-child]:mt-0 ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        disallowedElements={[]}
        skipHtml={false}
        components={{
          h1: ({ children }) => (
            <h1 className={`${sp.h1sz} font-bold ${sp.h1mb} ${sp.h1mt} text-gray-900 block`}>
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className={`${sp.h2sz} font-semibold ${sp.h2mb} ${sp.h2mt} text-gray-900 block`}>
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className={`${sp.h3sz} font-semibold ${sp.h3mb} ${sp.h3mt} text-gray-900 block`}>
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className={`${sp.h4sz} font-semibold ${sp.h4mb} ${sp.h4mt} text-gray-900 block`}>
              {children}
            </h4>
          ),
          h5: ({ children }) => (
            <h5 className={`text-sm font-semibold ${compact ? 'mb-0.5 mt-1.5' : 'mb-3 mt-4'} text-gray-900 block`}>
              {children}
            </h5>
          ),
          h6: ({ children }) => (
            <h6 className={`text-xs font-semibold ${compact ? 'mb-0.5 mt-1' : 'mb-2 mt-3'} text-gray-900 block`}>
              {children}
            </h6>
          ),
          p: ({ children }) => (
            <p className={`${sp.pmb} ${sp.pmt} ${sp.lead} text-gray-700 block`}>
              {children}
            </p>
          ),
          a: ({ href, children }) => (
            <a
              href={getAbsoluteUrl(href)}
              target="_blank"
              rel="noopener noreferrer"
              className={`${colors.link} underline`}
            >
              {children}
            </a>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-gray-900">
              {children}
            </strong>
          ),
          em: ({ children }) => (
            <em className="italic text-gray-800">
              {children}
            </em>
          ),
          ul: ({ children }) => (
            <ul className={`list-disc list-outside ${compact ? 'pl-5 space-y-2.5' : 'pl-6 space-y-3'} ${sp.ulmb} ${sp.ulmt} block`}>
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className={`list-decimal list-outside ${compact ? 'pl-5 space-y-2.5' : 'pl-6 space-y-3'} ${sp.ulmb} ${sp.ulmt} block`}>
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className={`${sp.limb} text-gray-700 ${compact ? 'leading-relaxed' : 'leading-7'}`}>
              {children}
            </li>
          ),
          img: ({ src, alt }) => {
            let imageUrl = src || '';

            // Convert relative URLs to absolute first
            if (imageUrl && !imageUrl.startsWith('http')) {
              imageUrl = getAbsoluteUrl(imageUrl);
            }

            return (
              <img
                src={imageUrl}
                alt={alt || 'Image'}
                className={`max-w-full h-auto rounded-lg ${compact ? 'my-2' : 'my-4'} mx-auto shadow-sm hover:shadow-md transition-shadow duration-200`}
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  console.warn('Failed to load image:', imageUrl);
                }}
              />
            );
          },
          blockquote: ({ children }) => (
            <blockquote className={`border-l-4 ${colors.blockquote} ${sp.bqpl} italic text-gray-600 ${sp.bqmy} bg-gray-50 rounded-r-lg block ${sp.lead}`}>
              {children}
            </blockquote>
          ),
          code: ({ children, inline }: any) => {
            if (inline) {
              return (
                <code className={`bg-gray-100 px-1.5 py-0.5 rounded font-sans ${sp.codesz} text-gray-800`}>
                  {children}
                </code>
              );
            }
            // Block code inside <pre> — no bg/padding/rounded, pre handles that
            return (
              <code className={`font-sans ${sp.codesz} block whitespace-pre-wrap break-words`}>
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <pre className={`bg-gray-100 ${sp.presz} rounded-lg overflow-x-auto ${sp.premy} block`}>
              {children}
            </pre>
          ),
          table: ({ children }) => (
            <div className={`${sp.tblmy} overflow-x-auto`}>
              <table className={`${compact ? 'text-xs' : 'min-w-full'} divide-y divide-gray-200 border border-gray-200`}>
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-gray-50">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody className="bg-white divide-y divide-gray-200">
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr className="hover:bg-gray-50">
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th className={`${compact ? 'px-2 py-1 text-[11px] font-semibold text-gray-600' : 'px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider'} text-left whitespace-nowrap`}>
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className={`${compact ? 'px-2 py-1 text-xs' : 'px-4 py-3 text-sm'} text-gray-700`}>
              {children}
            </td>
          ),
          hr: () => (
            <hr className={`${sp.hrmy} border-t-2 border-gray-300 block`} />
          )
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;